import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { router } from 'expo-router';
import Head from 'expo-router/head';
import { Camera, Moon, Bell, ShieldCheck, Ticket, UserPlus, Home, Send } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, CONTENT_MAX_WIDTH } from '../../src/theme';
import { Header } from '@components/layout';
import { useConfirm } from '@components/common';
import { RoleSelectionSheet } from '../../src/components/profile/RoleSelectionSheet';
import { AudienceInviteSheet } from '../../src/components/invites/AudienceInviteSheet';
import { AnonUpgradeBanner } from '../../src/components/profile/AnonUpgradeBanner';
import { IdentityCard } from '../../src/components/profile/IdentityCard';
import { StatsRow } from '../../src/components/profile/StatsRow';
import { ClaimedPropertiesCarousel } from '../../src/components/profile/ClaimedPropertiesCarousel';
import { RolesNudgeCard } from '../../src/components/profile/RolesNudgeCard';
import { SettingsGroup } from '../../src/components/profile/SettingsGroup';
import { ProfileFooter } from '../../src/components/profile/ProfileFooter';
import { useAuthStore } from '../../src/store/authStore';
import { useStructuresStore } from '../../src/store/structuresStore';
import { useOutboxStore } from '../../src/store/outboxStore';
import { shareAppInvite } from '../../src/utils/share';
import { Structure } from '../../src/types';
import { useThemeStore } from '../../src/store/themeStore';
import { useUIStore } from '../../src/store/uiStore';
import { appAlert } from '../../src/utils/appAlert';

const ROLES_NUDGE_THRESHOLD = 3;

export default function ProfileScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user, signOut, isLoading, isModerator } = useAuthStore();
  const {
    claimedStructureIds,
    fetchClaimedStructures,
    fetchStructuresByIds,
    isLoading: isLoadingStructures,
  } = useStructuresStore();
  const { mode } = useThemeStore();
  const confirm = useConfirm();
  const { openSheet, hasDismissedRolesNudge, hydrateRolesNudge, dismissRolesNudge } = useUIStore();

  const [claimedStructures, setClaimedStructures] = useState<Structure[]>([]);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [invitesOpen, setInvitesOpen] = useState(false);

  const isAnonScout = user?.isAnonymous === true;

  useEffect(() => {
    void hydrateRolesNudge();
  }, [hydrateRolesNudge]);

  const onlyFireScout =
    !user?.roles ||
    user.roles.length === 0 ||
    (user.roles.length === 1 && user.roles[0] === 'fire-scout');
  const showRolesNudge =
    !isAnonScout &&
    hasDismissedRolesNudge === false &&
    onlyFireScout &&
    (user?.postsCount ?? 0) >= ROLES_NUDGE_THRESHOLD;

  useEffect(() => {
    if (user) {
      fetchClaimedStructures(user.id);
    }
  }, [user, fetchClaimedStructures]);

  useEffect(() => {
    let cancelled = false;
    fetchStructuresByIds(claimedStructureIds.slice(0, 3)).then((results) => {
      if (!cancelled) setClaimedStructures(results);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
  }, [claimedStructureIds]);

  const handleSignOut = async () => {
    // Items are per-uid and durable (see docs/plans — Durable Offline
    // Outbox), so nothing is lost either way — this is a heads-up, not a
    // data-loss warning.
    const pendingCount = useOutboxStore.getState().pendingCount;
    const message =
      pendingCount > 0
        ? `You have ${pendingCount} observation${pendingCount === 1 ? '' : 's'} waiting to upload. They will upload next time you sign in. Sign out anyway?`
        : 'You will be returned to the sign-in screen.';
    const confirmed = await confirm({
      title: 'Sign out?',
      message,
      confirmLabel: 'Sign Out',
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await signOut();
    } catch {
      appAlert('Error', 'Failed to sign out');
    }
  };

  const appearanceValue = mode === 'system' ? 'System' : mode === 'dark' ? 'Dark' : 'Light';

  const activity = [
    {
      icon: Home,
      label: 'My Properties',
      onPress: () => router.push('/profile/my-properties'),
      badge: claimedStructureIds.length > 0 ? `${claimedStructureIds.length}` : undefined,
    },
    {
      icon: Camera,
      label: 'My Posts',
      onPress: () => router.push('/feed?filter=mine'),
      badge: user?.postsCount ? `${user.postsCount}` : undefined,
    },
  ];

  const community = [
    {
      icon: UserPlus,
      label: 'Invite a Friend',
      onPress: () => {
        void shareAppInvite().then((result) => {
          if (result === 'copied') {
            useUIStore.getState().showToast({ message: 'Link copied' });
          }
          if (result === 'failed') {
            useUIStore.getState().showToast({ message: "Couldn't copy the link" });
          }
        });
      },
    },
    ...(!user?.invitedByAgentId && !user?.agentProfile
      ? [
          {
            icon: Ticket,
            label: 'Enter Invite Code',
            onPress: () => router.push('/profile/invite-code' as never),
          },
        ]
      : []),
  ];

  const account = [
    // Moderator only: the audience invite emails go to people who are not in
    // REDI and never asked to be, so the row is not on anyone else's profile.
    // isModerator reads the server-set custom claim, not a roles entry.
    ...(isModerator
      ? [
          {
            icon: Send,
            label: 'Invite people to REDI',
            onPress: () => setInvitesOpen(true),
          },
        ]
      : []),
    ...(user?.agentProfile
      ? [
          {
            icon: ShieldCheck,
            label: `Agent: ${user.agentProfile.insurerName}`,
            onPress: () => router.push('/agent'),
          },
        ]
      : []),
    {
      icon: Moon,
      label: 'Appearance',
      value: appearanceValue,
      onPress: () => router.push('/profile/appearance'),
    },
    {
      icon: Bell,
      label: 'Notifications',
      onPress: () => router.push('/profile/notifications'),
    },
  ];

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Profile - Redi</title>
      </Head>
      {/* Icons-only top bar (mockup dethead) — the identity card's light name
          carries the screen, like post detail and create. The header avatar
          would only repeat the one directly below it. */}
      <Header showBack showAvatar={false} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        <View style={[styles.content, { paddingBottom: insets.bottom + 100 }]}>
          {/* Anonymous Fire Scout upgrade banner. Surfaces above the
              identity card because the identity card has nothing useful
              to show for an anon user (no name, no email). */}
          {isAnonScout && <AnonUpgradeBanner onUpgrade={() => openSheet('upgradeAccount')} />}

          <IdentityCard
            user={user}
            isAnonScout={isAnonScout}
            onEditProfile={() => router.push('/profile/account')}
          />

          <StatsRow
            postsCount={user?.postsCount || 0}
            propertiesCount={claimedStructureIds.length}
          />

          {/* My Properties Preview */}
          {(claimedStructureIds.length > 0 ||
            (isLoadingStructures && claimedStructureIds.length === 0)) && (
            <ClaimedPropertiesCarousel
              claimedStructureIds={claimedStructureIds}
              claimedStructures={claimedStructures}
              isLoadingStructures={isLoadingStructures}
            />
          )}

          {/* One-time "pick more roles" nudge for Fire Scouts who've been
              posting but haven't broadened their role set. Replaces the old
              forced role-selection screen. Dismissed permanently via the X. */}
          {showRolesNudge && (
            <RolesNudgeCard onPickRoles={() => setRolesOpen(true)} onDismiss={dismissRolesNudge} />
          )}

          <SettingsGroup title="Activity" items={activity} />
          <SettingsGroup title="Community" items={community} />
          <SettingsGroup title="Account" items={account} />

          <ProfileFooter onSignOut={handleSignOut} isLoading={isLoading} />
        </View>
      </ScrollView>
      {/* UpgradeAccountSheet is mounted once globally (UserMenuDropdown in
          the root layout) and opens via openSheet('upgradeAccount') — a
          second mount here would present two sheets at once. */}
      <RoleSelectionSheet visible={rolesOpen} onClose={() => setRolesOpen(false)} />
      <AudienceInviteSheet visible={invitesOpen} onClose={() => setInvitesOpen(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },
});
